import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';

export interface User {
  id: string | number;
  name: string;
  email: string;
  role: 'STUDENT' | 'COACH' | 'ADMIN' | string;
  status?: string;
  [key: string]: any;
}

export interface Session {
  user: User;
  expires?: string;
}

export type AuthStatus = 'authenticated' | 'unauthenticated' | 'loading';

interface AuthContextType {
  session: Session | null;
  status: AuthStatus;
  token: string | null;
  login: (token: string, user: User) => void;
  logout: () => void;
  updateSession: (updatedUser?: Partial<User>) => Promise<void>;
  profileChecked: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [token, setToken] = useState<string | null>(localStorage.getItem('access_token'));
  const [user, setUser] = useState<User | null>(null);
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [profileChecked, setProfileChecked] = useState(false);

  useEffect(() => {
    const storedToken = localStorage.getItem('access_token');
    const storedUser = localStorage.getItem('user_profile');

    if (storedToken && storedUser) {
      try {
        setToken(storedToken);
        const initialUser = JSON.parse(storedUser);
        setUser(initialUser);
        setStatus('authenticated');
        
        // Sync user details in the background to ensure we have the latest membership/tier from Admin updates
        api.get('/auth/me').then(res => {
          if (res.data) {
            setUser(prev => {
              const updated = { ...prev, ...res.data, hasProfile: prev?.hasProfile };
              localStorage.setItem('user_profile', JSON.stringify(updated));
              return updated as User;
            });
          }
        }).catch(err => console.error("Session sync failed:", err));
      } catch (e) {
        console.error('Failed to parse stored auth details', e);
        logout();
      }
    } else {
      setStatus('unauthenticated');
      setProfileChecked(true);
    }
  }, []);

  useEffect(() => {
    const checkProfile = async () => {
      if (status === 'authenticated' && user && token) {
        if (String(user.role).toUpperCase() === 'STUDENT') {
          try {
            const res = await api.get(`/auth/status?userId=${user.id}`);
            const profileExists = !!(res.data && res.data.profile);
            
            setUser(prev => {
              if (!prev) return null;
              const updated = { ...prev, hasProfile: profileExists };
              localStorage.setItem('user_profile', JSON.stringify(updated));
              return updated;
            });
          } catch (e) {
            console.error('Failed to fetch business profile status:', e);
            // Default to true on connection failure so we don't block them forever
            setUser(prev => prev ? { ...prev, hasProfile: prev.hasProfile ?? true } : null);
          } finally {
            setProfileChecked(true);
          }
        } else {
          setProfileChecked(true);
        }
      }
    };
    checkProfile();
  }, [status, token, user?.id]);

  const login = (newToken: string, newUser: User) => {
    localStorage.setItem('access_token', newToken);
    localStorage.setItem('user_profile', JSON.stringify(newUser));
    setToken(newToken);
    setUser(newUser);
    setProfileChecked(false); // Trigger check for new user
    setStatus('authenticated');
  };

  const logout = async () => {
    try {
      const res = await api.post('/auth/logout', {
        redirect_back: window.location.origin + '/login'
      });
      
      localStorage.removeItem('access_token');
      localStorage.removeItem('user_profile');
      
      if (res.data && res.data.logout_url) {
        window.location.href = res.data.logout_url;
        return; // Halt execution so React doesn't unmount and cancel the redirection
      }

      setToken(null);
      setUser(null);
      setProfileChecked(true);
      setStatus('unauthenticated');
    } catch (e) {
      console.error('Failed to logout via SSO', e);
      // Fallback local logout
      localStorage.removeItem('access_token');
      localStorage.removeItem('user_profile');
      setToken(null);
      setUser(null);
      setProfileChecked(true);
      setStatus('unauthenticated');
    }
  };

  const updateSession = async (updatedUser?: Partial<User>) => {
    if (!user) return;

    try {
      const res = await api.get('/auth/me');
      if (res.data) {
        let freshUser = { ...user, ...res.data, hasProfile: user.hasProfile };
        if (updatedUser && Object.keys(updatedUser).length > 0) {
          freshUser = { ...freshUser, ...updatedUser };
        }
        localStorage.setItem('user_profile', JSON.stringify(freshUser));
        setUser(freshUser as User);
        return;
      }
    } catch (err) {
      console.error("Failed to sync session from server:", err);
    }

    // Fallback to purely local merge if the network request fails
    if (updatedUser && Object.keys(updatedUser).length > 0) {
      const mergedUser = { ...user, ...updatedUser };
      localStorage.setItem('user_profile', JSON.stringify(mergedUser));
      setUser(mergedUser as User);
    }
  };

  const session: Session | null = user ? { user } : null;

  return (
    <AuthContext.Provider value={{ session, status, token, login, logout, updateSession, profileChecked }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// High-fidelity alias to mimic NextAuth hook exactly, minimizing refactoring changes in views
export const useSession = () => {
  const { session, status, updateSession } = useAuth();
  return {
    data: session,
    status,
    update: updateSession,
  };
};
